import { useReducer, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Divider, Button, CircularProgress } from '@mui/material';
import { Formik, Field, FieldProps, Form, FormikHelpers } from 'formik';
import { updateUserData, getPasswordPolicy } from 'store/myAccount/requests';
import { useMutation, useQuery } from '@redux-requests/react';
import GVPopper from 'components/common/GVPopper/GVPopper';
import GVTypography from 'components/lib/GVTypography/GVTypography';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import MyAccountCard from '../Card';
import PasswordStrengthCheck, { PasswordRules, anyRuleFails } from './PasswordStrengthCheck';
import styles from './Security.module.css';

let passwordSheriff: any;
import('password-sheriff').then((module) => {
  passwordSheriff = module.default || module;
});

enum PolicyPasswordField {
  UPDATE_PASSWORD = 'updatePassword',
  CONFIRM_PASSWORD = 'confirmPassword',
}

export interface SecurityFormData {
  currentPassword: string;
  [PolicyPasswordField.UPDATE_PASSWORD]: string;
  [PolicyPasswordField.CONFIRM_PASSWORD]: string;
}

interface RuleState {
  [PolicyPasswordField.UPDATE_PASSWORD]?: PasswordRules;
  [PolicyPasswordField.CONFIRM_PASSWORD]?: PasswordRules;
}

const rulesReducer = (state: RuleState, action: { type: string; payload: any }) => {
  switch (action.type) {
    case PolicyPasswordField.CONFIRM_PASSWORD:
      return { ...state, [PolicyPasswordField.CONFIRM_PASSWORD]: action.payload };
    case PolicyPasswordField.UPDATE_PASSWORD:
      return { ...state, [PolicyPasswordField.UPDATE_PASSWORD]: action.payload };
    default:
      throw Error();
  }
};

const Security = () => {
  const dispatch = useDispatch();

  const updatePasswordEl = useRef(null);
  const confirmPasswordEl = useRef(null);

  const { loading: loadingSaveChanges } = useMutation(updateUserData);

  const passwordPolicyQuery = useQuery(getPasswordPolicy());
  const passwordPolicy = passwordPolicyQuery.data || {};

  const updatePassword = PolicyPasswordField.UPDATE_PASSWORD;
  const confirmPassword = PolicyPasswordField.CONFIRM_PASSWORD;

  const initialValues: SecurityFormData = {
    currentPassword: '',
    [updatePassword]: '',
    [confirmPassword]: '',
  };

  // The password policy rules are stored separately depending of the field - that way we can control opening of the validator individually
  const [rules, dispatchRules] = useReducer(rulesReducer, {
    [updatePassword]: [],
    [confirmPassword]: [],
  });

  // We need to control whether a field is focused or not to open the password strength popup only for the active field
  const [focused, setFocused] = useState('');

  /**
   * function to validate if the password matches the policy and to obtain a list of rules to let the user know what they are missing
   * We return ' ' in order to display the error indicator on the field without having to display a message underneath
   */
  const validatePasswordPolicy = (fieldName: string, password: string): string => {
    const policy = passwordSheriff(passwordPolicy.type);
    const passwordRules = policy.missing(password).rules;
    dispatchRules({ type: fieldName, payload: passwordRules });
    if (!policy.check(password)) return ' ';
    return '';
  };

  const validate = (values: SecurityFormData) => {
    const errors: Partial<SecurityFormData> = {};
    const error = 'Required field';
    if (!values.currentPassword.trim()) {
      errors.currentPassword = error;
    }
    if (values.currentPassword && !values[updatePassword]) {
      errors[updatePassword] = error;
    }
    if (values.currentPassword && !values[confirmPassword]) {
      errors[confirmPassword] = error;
    }
    if (values[updatePassword] && values[confirmPassword] && values[updatePassword] !== values[confirmPassword]) {
      const mismatchError = 'Passwords mismatch';
      errors[confirmPassword] = mismatchError;
      errors[updatePassword] = mismatchError;
    }
    return errors;
  };

  const onSubmit = (values: SecurityFormData, actions: FormikHelpers<SecurityFormData>) => {
    const newValues = { currentPassword: values.currentPassword, newPassword: values[confirmPassword] };
    dispatch(updateUserData(newValues, undefined, actions));
  };

  return (
    <MyAccountCard title={<GVTypography variant="h6">Change Password</GVTypography>}>
      <GVPopper
        anchorElement={updatePasswordEl.current}
        open={focused === updatePassword && anyRuleFails(rules[updatePassword])}
      >
        <PasswordStrengthCheck passwordRules={rules[updatePassword]} />
      </GVPopper>
      <GVPopper
        anchorElement={confirmPasswordEl.current}
        open={focused === confirmPassword && anyRuleFails(rules[confirmPassword])}
      >
        <PasswordStrengthCheck passwordRules={rules[confirmPassword]} />
      </GVPopper>
      <Divider light />
      <Formik key="settingsForm" initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
        {(props) => (
          <Form onSubmit={props.handleSubmit} className={styles.form}>
            <GVTypography variant="subtitle1">Current Password</GVTypography>
            <Field name="currentPassword" data-testid="current_password_field">
              {(fieldProps: FieldProps) => (
                <GVTextField
                  name={fieldProps.field.name}
                  hasError={!!fieldProps.meta.error}
                  message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                  onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                  label="Password"
                  value={fieldProps.field.value}
                  type="password"
                />
              )}
            </Field>
            <div className={styles.divider}>
              <Divider light />
            </div>
            <div className={styles.inputContainer}>
              <GVTypography variant="subtitle1" className={styles.heading}>
                Update Password
              </GVTypography>
              <Field
                validate={(password: string) => validatePasswordPolicy(updatePassword, password)}
                name={updatePassword}
              >
                {(fieldProps: FieldProps) => (
                  <GVTextField
                    onFocus={() => setFocused(updatePassword)}
                    onBlur={() => setFocused('')}
                    ref={updatePasswordEl}
                    name={fieldProps.field.name}
                    hasError={!!fieldProps.meta.error}
                    message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                    onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                    label="New Password"
                    value={fieldProps.field.value}
                    type="password"
                    data-testid="new_password_field"
                  />
                )}
              </Field>
              <Field
                validate={(password: string) => validatePasswordPolicy(confirmPassword, password)}
                name={confirmPassword}
              >
                {(fieldProps: FieldProps) => (
                  <GVTextField
                    onFocus={() => setFocused(confirmPassword)}
                    onBlur={() => setFocused('')}
                    ref={confirmPasswordEl}
                    name={fieldProps.field.name}
                    hasError={!!fieldProps.meta.error}
                    message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                    onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                    label="Confirm Password"
                    value={fieldProps.field.value}
                    type="password"
                    data-testid="confirm_password_field"
                  />
                )}
              </Field>
            </div>
            <div className={styles.divider}>
              <Divider light />
            </div>
            <div className={styles.buttonContainer}>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={!props.dirty || anyRuleFails(rules[updatePassword]) || anyRuleFails(rules[confirmPassword])}
                data-testid="change_password_field"
              >
                Change Password
                {loadingSaveChanges && <CircularProgress className={styles.savingSpinner} size={16} color="primary" />}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </MyAccountCard>
  );
};

export default Security;
